import React, { useContext } from 'react';
import { ThemeContext } from 'styled-components';
import { IconMinus } from 'wg-fe-ui';
import { handleResponseLanguage } from '../../../../../../../i18n';
import {
  Text,
  Input,
  Box,
  Grid,
  Flex,
  Tooltip,
  Icon,
} from '@wegroup/design-system';
import TooltipCircle from '../../../../../../common/src/components/TooltipCircle';
import { FlowsQuestion } from '../../../../types/FlowsTypes';
import useFlowInputComponent from '../../../../hooks/useFlowsInputComponent';

interface Props {
  question: FlowsQuestion;
}

const AmountInputComponent: React.FC<React.PropsWithChildren<Props>> = ({
  question,
}) => {
  const theme = useContext(ThemeContext);
  const icons = {
    // rooms
    living_rooms: 'IconFurnitureFilled',
    kitchens: 'IconKitchenFilled',
    bedrooms: 'IconBedroomFilled',
    bathrooms: 'IconBathroomFilled',
    private_office: 'IconPrivateHomeOfficeFilled',
    play_room: 'IconPlayroomFilled',
    hobby_room: 'IconFurnitureFilled',
    veranda: 'IconFurnitureFilled',
    professional_office: 'IconFurnitureFilled',
    washing_room: 'IconWashingRoomFilled',
    boiler: 'IconFurnitureFilled',
    storage_rooms: 'IconStorageRoomFilled',
    dressing: 'IconDressingFilled',
    garages: 'IconGarageFilled',
    wellness_room: 'IconWellnessRoomFilled',
    other_rooms: 'IconFurnitureFilled',
    // other
    number_of_vehicles: 'IconCarFilled',
    RESIDENCE: 'GeneralRiskObjectsHome',
    FAMILY_CAR: 'GeneralRiskObjectsCar',
    BUILDING: 'RiskObjectsPropertyShed',
  };

  const defaultValues = {
    'home.rooms_living_rooms': 1,
    'home.rooms_kitchens': 1,
    'home.rooms_bedrooms': 2,
    'home.rooms_bathrooms': 1,
  };

  const {
    field: { value, onChange, onBlur },
  } = useFlowInputComponent({
    question,
    defaultValue: defaultValues[question.id] || 0,
  });

  const inputValue = value as number;
  const minValue = 0;
  const maxValue = 200;

  const handleInputChange = (val: React.ReactText, blur?: boolean) => {
    if ((val <= maxValue && val >= minValue) || val === '') {
      blur && onBlur({ name: question.id, value: val });
      onChange({ name: question.id, value: val });
    }
  };

  const handleButtonClick = (type: string) => {
    handleInputChange(
      type === 'plus' ? Number(inputValue) + 1 : Number(inputValue) - 1,
      true,
    );
  };

  const handleOnBlur = () => {
    handleInputChange(Number(inputValue), true);
  };

  return (
    <Box
      data-test-id="ProdFac_AmountInputComponent"
      border="1px solid #F0F1F3"
      boxShadow="level2"
      bg="white"
      borderRadius="md"
      overflow="hidden"
      maxW="md"
      mx="auto"
    >
      <Flex p="4" alignItems="center" justifyContent="space-between">
        <Flex alignItems="center">
          <Flex
            bg="primary.50"
            w="4.4rem"
            h="4.4rem"
            justifyContent="center"
            alignItems="center"
            borderRadius="md"
            flexShrink={0}
          >
            <Icon
              name={
                icons[question?.icon_key || question?.field || ''] ||
                question?.icon_key
              }
              color="primary.500"
            />
          </Flex>
          <Text ml="4" textStyle="normalBody">
            {handleResponseLanguage(question?.title)}
          </Text>
        </Flex>
        {question.description && (
          <Tooltip label={handleResponseLanguage(question.description)}>
            <TooltipCircle>?</TooltipCircle>
          </Tooltip>
        )}
      </Flex>
      <Grid
        h="5rem"
        templateColumns="1fr 1fr 1fr"
        borderTop="1px solid #F0F1F3"
      >
        <Flex
          bg="#F0F1F3"
          borderRight="gr"
          alignItems="center"
          justifyContent="center"
          h="100%"
          onClick={() => {
            handleButtonClick('minus');
          }}
          data-test-id="ProdFac_AmountInputComponent_MinusButton"
          cursor="pointer"
          _active={{ bg: 'gray.200' }}
          transition="0.2s"
        >
          <IconMinus color={theme.gray[200]} />
        </Flex>
        <Input
          name={question.id}
          h="100%"
          border="none"
          type="text"
          fontWeight="bold"
          fontSize="2.1rem"
          textAlign="center"
          onBlur={handleOnBlur}
          value={value as number}
          onChange={(event: { currentTarget: { value: string } }) => {
            handleInputChange(event?.currentTarget?.value);
          }}
          data-test-id="ProdFac_AmountInputComponent_InputField"
        />
        <Flex
          h="100%"
          bg="primary.50"
          alignItems="center"
          justifyContent="center"
          // disabled={inputValue >= maxValue}
          onClick={() => {
            handleButtonClick('plus');
          }}
          data-test-id="ProdFac_AmountInputComponent_PlusButton"
          cursor="pointer"
          _active={{ bg: 'primary.100' }}
          transition="0.2s"
        >
          <Icon name="IconActionPlus" color="primary.500" />
        </Flex>
      </Grid>
    </Box>
  );
};

export default AmountInputComponent;
